/**
 * PostMatchRenderer — post-match summary overlay.
 *
 * Replaces the old bare "PLAYER WINS!" text with a full stats screen:
 * winner banner, per-player stat cards, awards, and rematch/menu buttons.
 * Staggered entrance animation over 1.5s.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { POST_MATCH, PLAYER_COLORS, WORLD, SURVIVAL } from '../../constants';
import { lookupTank } from '../../tanks';
import { getActiveTheme } from '../../stages/registry';
import type { GameState, MatchStats } from '../../types';
import { getCoins } from '../../progression/storage';
import { computeCoinAward } from '../../progression/coins';

const PM = POST_MATCH;

// ─── Award definitions ──────────────────────────────────────────

interface Award {
  icon: string;
  label: string;
  playerIndex: number;
  detail: string;
}

function computeAwards(state: GameState): Award[] {
  const awards: Award[] = [];
  const stats = state.matchStats;
  if (!stats) return awards;

  // HEAVY HITTER — highest single damage hit
  let bestHitPlayer = -1;
  let bestHitVal = 0;
  for (let i = 0; i < stats.bestSingleHit.length; i++) {
    if (stats.bestSingleHit[i] > bestHitVal) {
      bestHitVal = stats.bestSingleHit[i];
      bestHitPlayer = i;
    }
  }
  if (bestHitPlayer >= 0 && bestHitVal >= 15) {
    awards.push({
      icon: '\u{1F4A5}',
      label: 'HEAVY HITTER',
      playerIndex: bestHitPlayer,
      detail: `${Math.round(bestHitVal)}% single hit`,
    });
  }

  // UNTOUCHABLE — winner took < 25% damage
  if (state.winner !== null) {
    const winnerDmg = state.players[state.winner].damagePercent;
    if (winnerDmg < 25) {
      awards.push({
        icon: '\u{1F6E1}',
        label: 'UNTOUCHABLE',
        playerIndex: state.winner,
        detail: `Won with only ${Math.round(winnerDmg)}% taken`,
      });
    }
  }

  // VOID MASTER — 2+ void kills
  for (let i = 0; i < stats.voidKills.length; i++) {
    if (stats.voidKills[i] >= 2) {
      awards.push({
        icon: '\u{2B07}',
        label: 'VOID MASTER',
        playerIndex: i,
        detail: `${stats.voidKills[i]} void kills`,
      });
    }
  }

  // SKY LAUNCHER — any sky kills
  for (let i = 0; i < stats.skyKills.length; i++) {
    if (stats.skyKills[i] >= 1) {
      awards.push({
        icon: '\u{2B06}',
        label: 'SKY LAUNCHER',
        playerIndex: i,
        detail: `${stats.skyKills[i]} sky kill${stats.skyKills[i] > 1 ? 's' : ''}`,
      });
    }
  }

  // MARKSMAN — high damage-per-shot ratio
  for (let i = 0; i < stats.shotsFired.length; i++) {
    if (stats.shotsFired[i] > 0) {
      const avgDmg = stats.damageDealt[i] / stats.shotsFired[i];
      if (avgDmg >= 20 && stats.shotsFired[i] >= 2) {
        awards.push({
          icon: '\u{1F3AF}',
          label: 'MARKSMAN',
          playerIndex: i,
          detail: `${Math.round(avgDmg)}% avg per shot`,
        });
      }
    }
  }

  // STYLE KING — highest totalTrickScore >= 5000
  let bestStylePlayer = -1;
  let bestStyleVal = 0;
  for (let i = 0; i < stats.totalTrickScore.length; i++) {
    if (stats.totalTrickScore[i] > bestStyleVal) {
      bestStyleVal = stats.totalTrickScore[i];
      bestStylePlayer = i;
    }
  }
  if (bestStylePlayer >= 0 && bestStyleVal >= 5000) {
    awards.push({
      icon: '\u{1F451}',
      label: 'STYLE KING',
      playerIndex: bestStylePlayer,
      detail: `${bestStyleVal.toLocaleString()} style pts`,
    });
  }

  // COMBO MASTER — best combo >= 3
  for (let i = 0; i < stats.bestCombo.length; i++) {
    if (stats.bestCombo[i] >= 3) {
      awards.push({
        icon: '\u{1F525}',
        label: 'COMBO MASTER',
        playerIndex: i,
        detail: `${stats.bestCombo[i]}x combo`,
      });
    }
  }

  // Return top 3
  return awards.slice(0, 3);
}

// ─── Damage color (from HUD spec) ───────────────────────────────

function getDamageColor(percent: number): number {
  if (percent < 50) return 0x00ff00;
  if (percent < 100) return 0xffdd00;
  if (percent < 150) return 0xff8800;
  return 0xff0044;
}

// ─── Easing ─────────────────────────────────────────────────────

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function clamp01(t: number): number {
  return Math.max(0, Math.min(1, t));
}

// ─── PostMatchRenderer ──────────────────────────────────────────

export class PostMatchRenderer {
  private container: Container;

  // Overlay
  private overlay: Graphics;

  // Winner title
  private winnerText: Text;
  private roundText: Text;

  // Stat cards (2 players)
  private cardContainers: Container[];

  // Awards
  private awardContainer: Container;

  // Buttons
  private buttonContainer: Container;
  private rematchBg: Graphics;
  private menuBg: Graphics;
  private rematchText: Text;
  private menuText: Text;

  // Opponent status line (multiplayer only)
  private statusText: Text;

  // Coin award text
  private coinText: Text;

  // State
  private visible = false;
  private prevPhase = '';

  // Touch handler
  private tapVirtualKey: (code: string) => void;

  constructor(uiContainer: Container, tapVirtualKey: (code: string) => void) {
    this.tapVirtualKey = tapVirtualKey;

    this.container = new Container();
    this.container.zIndex = 250; // above HUD (200) and menu UI (150)
    this.container.visible = false;
    uiContainer.addChild(this.container);

    // Dark overlay
    this.overlay = new Graphics();
    this.container.addChild(this.overlay);

    // Winner title
    this.winnerText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"Arial Black", "Impact", sans-serif',
        fontSize: PM.TITLE_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
        align: 'center',
        stroke: { color: 0x000000, width: 5 },
      }),
    });
    this.winnerText.anchor.set(0.5, 0.5);
    this.container.addChild(this.winnerText);

    // Round subtitle
    this.roundText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: PM.SUBTITLE_FONT_SIZE,
        fill: 0x888888,
        align: 'center',
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.roundText.anchor.set(0.5, 0);
    this.container.addChild(this.roundText);

    // Stat card containers (built dynamically per update)
    this.cardContainers = [];
    for (let i = 0; i < 2; i++) {
      const cc = new Container();
      this.container.addChild(cc);
      this.cardContainers.push(cc);
    }

    // Awards container
    this.awardContainer = new Container();
    this.container.addChild(this.awardContainer);

    // Buttons
    this.buttonContainer = new Container();
    this.container.addChild(this.buttonContainer);

    this.rematchBg = new Graphics();
    this.buttonContainer.addChild(this.rematchBg);
    this.rematchText = new Text({
      text: 'REMATCH',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: PM.BUTTON_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.rematchText.anchor.set(0.5, 0.5);
    this.buttonContainer.addChild(this.rematchText);

    this.menuBg = new Graphics();
    this.buttonContainer.addChild(this.menuBg);
    this.menuText = new Text({
      text: 'MENU',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: PM.BUTTON_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.menuText.anchor.set(0.5, 0.5);
    this.buttonContainer.addChild(this.menuText);

    // Coin award text (below round subtitle)
    this.coinText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 22,
        fontWeight: 'bold',
        fill: 0xffd700,
        align: 'center',
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.coinText.anchor.set(0.5, 0);
    this.coinText.visible = false;
    this.container.addChild(this.coinText);

    // Opponent status line (above buttons)
    this.statusText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 18,
        fontWeight: 'bold',
        fill: 0x888888,
        align: 'center',
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.statusText.anchor.set(0.5, 0.5);
    this.statusText.visible = false;
    this.container.addChild(this.statusText);

    // Touch handlers for buttons
    this.rematchBg.eventMode = 'static';
    this.rematchBg.cursor = 'pointer';
    this.rematchBg.on('pointerdown', () => {
      this.tapVirtualKey('ArrowLeft');
      setTimeout(() => this.tapVirtualKey('Space'), 50);
    });
    this.menuBg.eventMode = 'static';
    this.menuBg.cursor = 'pointer';
    this.menuBg.on('pointerdown', () => {
      this.tapVirtualKey('ArrowRight');
      setTimeout(() => this.tapVirtualKey('Space'), 50);
    });
  }

  hide(): void {
    this.container.visible = false;
    this.visible = false;
  }

  update(state: GameState, dt: number): void {
    if (state.phase !== 'game_over') {
      this.hide();
      this.prevPhase = state.phase;
      return;
    }

    // Show container
    this.container.visible = true;

    // Rebuild stat content on first frame of game_over
    if (this.prevPhase !== 'game_over') {
      this.rebuildContent(state);
    }
    this.prevPhase = state.phase;

    // Animation progress (gameOverTimer counts UP)
    const t = state.gameOverTimer;
    const cw = WORLD.CANVAS_WIDTH;
    const ch = WORLD.CANVAS_HEIGHT;
    const chrome = getActiveTheme(state.stageId).bumperBright;

    // 1. Overlay fade-in
    const overlayAlpha = clamp01(t / PM.OVERLAY_FADE_DURATION) * PM.OVERLAY_ALPHA;
    this.overlay.clear();
    this.overlay.rect(0, 0, cw, ch);
    this.overlay.fill({ color: 0x000000, alpha: overlayAlpha });

    // 2. Winner title punch-in
    const titleProgress = clamp01((t - PM.TITLE_PUNCH_DELAY) / PM.TITLE_PUNCH_DURATION);
    if (titleProgress > 0) {
      const scale = easeOutBack(titleProgress);
      this.winnerText.visible = true;
      this.winnerText.scale.set(scale);
      this.winnerText.alpha = clamp01(titleProgress * 3);
      this.winnerText.position.set(cw / 2, PM.TITLE_Y);

      this.roundText.visible = true;
      this.roundText.alpha = clamp01((titleProgress - 0.3) * 3);
      this.roundText.position.set(cw / 2, PM.SUBTITLE_Y);

      // Coin award text — fade in after subtitle, gentle pulse
      if (this.coinText.visible) {
        const coinAlpha = clamp01((titleProgress - 0.5) * 3);
        this.coinText.alpha = coinAlpha * (0.85 + 0.15 * Math.sin(Date.now() * 0.004));
        this.coinText.position.set(cw / 2, PM.SUBTITLE_Y + 28);
      }
    } else {
      this.winnerText.visible = false;
      this.roundText.visible = false;
    }

    // 3. Stat cards slide in from sides
    const cardProgress = clamp01((t - PM.CARDS_SLIDE_DELAY) / PM.CARDS_SLIDE_DURATION);
    for (let i = 0; i < this.cardContainers.length; i++) {
      if (i >= state.players.length) {
        this.cardContainers[i].visible = false;
        continue;
      }
      this.cardContainers[i].visible = true;
      const ease = easeOutCubic(cardProgress);
      const slideOffset = (1 - ease) * 300 * (i === 0 ? -1 : 1);
      const totalW = PM.CARD_WIDTH * 2 + PM.CARD_GAP;
      const baseX = (cw - totalW) / 2 + i * (PM.CARD_WIDTH + PM.CARD_GAP);
      this.cardContainers[i].position.set(baseX + slideOffset, PM.CARD_Y);
      this.cardContainers[i].alpha = ease;
    }

    // 4. Awards fade in
    const awardProgress = clamp01((t - PM.AWARDS_FADE_DELAY) / 0.3);
    this.awardContainer.visible = awardProgress > 0;
    this.awardContainer.alpha = easeOutCubic(awardProgress);
    this.awardContainer.position.set(cw / 2, PM.AWARDS_Y);

    // 5. Multiplayer status line + Buttons
    const btnProgress = clamp01((t - PM.BUTTONS_FADE_DELAY) / 0.2);
    const mp = state.multiplayer;
    const opponentGone = !!mp?.disconnectPhase;
    const opponentReady = !!mp?.opponentReady && !opponentGone;
    const rematchSent = mp?.lobbyStatus === 'Waiting for opponent...' && !opponentGone;

    if (mp?.enabled && btnProgress > 0) {
      this.statusText.visible = true;
      this.statusText.position.set(cw / 2, PM.BUTTON_Y - 34);

      if (opponentGone) {
        const name = mp.opponentName || 'Opponent';
        this.statusText.text = `${name.toUpperCase()} DISCONNECTED`;
        this.statusText.style.fill = 0xff4444;
        this.statusText.alpha = 1;
        // Force cursor to MENU when opponent is gone
        state.postMatchCursor = 1;
      } else if (rematchSent) {
        this.statusText.text = 'REMATCH SENT \u2014 Waiting for opponent...';
        this.statusText.style.fill = 0x00ffff;
        this.statusText.alpha = 0.6 + 0.4 * Math.sin(Date.now() * 0.004);
      } else if (opponentReady) {
        this.statusText.text = 'OPPONENT WANTS REMATCH!';
        this.statusText.style.fill = 0x00ff88;
        this.statusText.alpha = 0.7 + 0.3 * Math.sin(Date.now() * 0.005);
      } else {
        this.statusText.visible = false;
      }
    } else {
      this.statusText.visible = false;
    }

    // 6. Buttons fade in
    this.buttonContainer.visible = btnProgress > 0;
    this.buttonContainer.alpha = easeOutCubic(btnProgress);

    // Button layout + selection state
    if (btnProgress > 0) {
      const btnTotalW = PM.BUTTON_WIDTH * 2 + PM.BUTTON_GAP;
      const btnStartX = (cw - btnTotalW) / 2;
      const btnY = PM.BUTTON_Y;

      // Rematch button — disabled when opponent disconnected
      const remDisabled = opponentGone && !!mp?.enabled;
      const remSel = state.postMatchCursor === 0 && !remDisabled;
      this.rematchBg.clear();
      this.rematchBg.roundRect(btnStartX, btnY, PM.BUTTON_WIDTH, PM.BUTTON_HEIGHT, PM.BUTTON_CORNER_RADIUS);
      this.rematchBg.fill({ color: remSel ? chrome : 0x0a0a15, alpha: remSel ? 0.2 : 0.5 });
      this.rematchBg.roundRect(btnStartX, btnY, PM.BUTTON_WIDTH, PM.BUTTON_HEIGHT, PM.BUTTON_CORNER_RADIUS);
      this.rematchBg.stroke({ color: remDisabled ? 0x333333 : (remSel ? chrome : 0x444444), width: remSel ? 2.5 : 1.5, alpha: remDisabled ? 0.3 : (remSel ? 1.0 : 0.5) });
      this.rematchText.position.set(btnStartX + PM.BUTTON_WIDTH / 2, btnY + PM.BUTTON_HEIGHT / 2);
      this.rematchText.style.fill = remDisabled ? 0x333333 : (remSel ? 0xffffff : 0x888888);
      this.rematchBg.eventMode = remDisabled ? 'none' : 'static';
      this.rematchBg.cursor = remDisabled ? 'default' : 'pointer';

      // Menu button
      const menuSel = state.postMatchCursor === 1;
      this.menuBg.clear();
      const menuX = btnStartX + PM.BUTTON_WIDTH + PM.BUTTON_GAP;
      this.menuBg.roundRect(menuX, btnY, PM.BUTTON_WIDTH, PM.BUTTON_HEIGHT, PM.BUTTON_CORNER_RADIUS);
      this.menuBg.fill({ color: menuSel ? chrome : 0x0a0a15, alpha: menuSel ? 0.2 : 0.5 });
      this.menuBg.roundRect(menuX, btnY, PM.BUTTON_WIDTH, PM.BUTTON_HEIGHT, PM.BUTTON_CORNER_RADIUS);
      this.menuBg.stroke({ color: menuSel ? chrome : 0x444444, width: menuSel ? 2.5 : 1.5, alpha: menuSel ? 1.0 : 0.5 });
      this.menuText.position.set(menuX + PM.BUTTON_WIDTH / 2, btnY + PM.BUTTON_HEIGHT / 2);
      this.menuText.style.fill = menuSel ? 0xffffff : 0x888888;
    }
  }

  /**
   * Rebuild all text content when entering game_over.
   * Called once per game_over entry.
   */
  private rebuildContent(state: GameState): void {
    const chrome = getActiveTheme(state.stageId).bumperBright;

    // Survival mode — custom game over display
    if (state.modeState.mode === 'survival') {
      this.rebuildSurvivalContent(state, chrome);
      return;
    }

    // Winner title
    if (state.winner !== null) {
      const pColor = PLAYER_COLORS[state.winner];
      const tank = lookupTank(state.players[state.winner].tankId);
      const winnerName = state.players[state.winner].name;
      this.winnerText.text = `${winnerName}  W I N S !`;
      this.winnerText.style.fill = pColor;
      this.winnerText.style.stroke = { color: 0x000000, width: 5 };
    } else {
      this.winnerText.text = 'D R A W !';
      this.winnerText.style.fill = 0xffffff;
    }
    this.roundText.text = `Round ${state.roundNumber}`;

    // Coin award text (all modes)
    const coins = computeCoinAward(state);
    this.coinText.text = coins > 0 ? `+${coins} COINS` : '';
    this.coinText.visible = coins > 0;

    // Stat cards
    for (let i = 0; i < 2; i++) {
      const cc = this.cardContainers[i];
      // Destroy previous children
      while (cc.children.length > 0) {
        cc.children[0].destroy();
        cc.removeChildAt(0);
      }

      if (i >= state.players.length) continue;

      const p = state.players[i];
      const pColor = PLAYER_COLORS[i];
      const isWinner = state.winner === i;
      const tank = lookupTank(p.tankId);
      const stats = state.matchStats;

      // Card background
      const bg = new Graphics();
      bg.roundRect(0, 0, PM.CARD_WIDTH, PM.CARD_HEIGHT, PM.CARD_CORNER_RADIUS);
      bg.fill({ color: 0x0a0a15, alpha: PM.CARD_BG_ALPHA });
      bg.roundRect(0, 0, PM.CARD_WIDTH, PM.CARD_HEIGHT, PM.CARD_CORNER_RADIUS);
      bg.stroke({ color: isWinner ? pColor : chrome, width: PM.CARD_BORDER_WIDTH, alpha: isWinner ? 0.9 : 0.4 });
      cc.addChild(bg);

      // Winner glow overlay
      if (isWinner) {
        const glow = new Graphics();
        glow.roundRect(0, 0, PM.CARD_WIDTH, PM.CARD_HEIGHT, PM.CARD_CORNER_RADIUS);
        glow.fill({ color: pColor, alpha: 0.06 });
        cc.addChild(glow);
      }

      // Tank name header
      const nameText = new Text({
        text: `${p.name}  ${tank.name.toUpperCase()}`,
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: PM.TANK_NAME_FONT_SIZE,
          fontWeight: 'bold',
          fill: pColor,
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      nameText.position.set(16, 12);
      cc.addChild(nameText);

      // Winner badge
      if (isWinner) {
        const badge = new Text({
          text: 'WINNER',
          style: new TextStyle({
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: 11,
            fontWeight: 'bold',
            fill: 0xffd700,
            stroke: { color: 0x000000, width: 2 },
          }),
        });
        badge.anchor.set(1, 0);
        badge.position.set(PM.CARD_WIDTH - 12, 14);
        cc.addChild(badge);
      }

      // Divider line
      const divider = new Graphics();
      divider.moveTo(16, 40);
      divider.lineTo(PM.CARD_WIDTH - 16, 40);
      divider.stroke({ color: chrome, width: 1, alpha: 0.3 });
      cc.addChild(divider);

      // Stats rows
      const statRows = [
        { label: 'Eliminations', value: `${stats.kills[i]}` },
        { label: 'Damage Dealt', value: `${Math.round(stats.damageDealt[i])}%` },
        { label: 'Damage Taken', value: `${Math.round(p.damagePercent)}%`, color: getDamageColor(p.damagePercent) },
        { label: 'Best Hit', value: stats.bestSingleHit[i] > 0 ? `${Math.round(stats.bestSingleHit[i])}%` : '--' },
        { label: 'Shots Fired', value: `${stats.shotsFired[i]}` },
      ];

      let rowY = 52;
      for (const row of statRows) {
        const labelTx = new Text({
          text: row.label,
          style: new TextStyle({
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: PM.STAT_FONT_SIZE,
            fill: PM.STAT_LABEL_COLOR,
          }),
        });
        labelTx.position.set(16, rowY);
        cc.addChild(labelTx);

        const valTx = new Text({
          text: row.value,
          style: new TextStyle({
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: PM.STAT_VALUE_FONT_SIZE,
            fontWeight: 'bold',
            fill: row.color ?? PM.STAT_VALUE_COLOR,
            stroke: { color: 0x000000, width: 1 },
          }),
        });
        valTx.anchor.set(1, 0);
        valTx.position.set(PM.CARD_WIDTH - 16, rowY);
        cc.addChild(valTx);

        rowY += 28;
      }
    }

    // Awards
    while (this.awardContainer.children.length > 0) {
      this.awardContainer.children[0].destroy();
      this.awardContainer.removeChildAt(0);
    }

    const awards = computeAwards(state);
    if (awards.length > 0) {
      // "AWARDS" header
      const header = new Text({
        text: 'AWARDS',
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 14,
          fontWeight: 'bold',
          fill: PM.AWARD_COLOR,
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      header.anchor.set(0.5, 0);
      header.position.set(0, 0);
      this.awardContainer.addChild(header);

      let awardY = 28;
      for (const award of awards) {
        const pColor = PLAYER_COLORS[award.playerIndex];
        const row = new Text({
          text: `${award.icon}  ${award.label}    ${state.players[award.playerIndex].name}    ${award.detail}`,
          style: new TextStyle({
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: PM.AWARD_FONT_SIZE,
            fill: pColor,
            stroke: { color: 0x000000, width: 2 },
          }),
        });
        row.anchor.set(0.5, 0);
        row.position.set(0, awardY);
        this.awardContainer.addChild(row);
        awardY += PM.AWARD_ROW_HEIGHT;
      }
    }
  }

  /**
   * Survival-specific game over screen.
   * Shows kill count, trick score, grade, and high score.
   */
  private rebuildSurvivalContent(state: GameState, chrome: number): void {
    const ms = state.modeState;
    const kills = ms.survivalKills;
    const trickScore = state.trickScores[0] ?? 0;
    const hs = ms.survivalHighScore;
    const isNewBest = hs && kills === hs.kills && trickScore === hs.score;

    // Title
    this.winnerText.text = 'G A M E   O V E R';
    this.winnerText.style.fill = 0xff4444;
    this.winnerText.style.stroke = { color: 0x000000, width: 5 };

    // Subtitle — kill count
    this.roundText.text = `${kills} Kill${kills !== 1 ? 's' : ''}`;

    // Coin award text
    const coins = computeCoinAward(state);
    this.coinText.text = coins > 0 ? `+${coins} COINS` : '';
    this.coinText.visible = coins > 0;

    // Grade
    let grade = 'D';
    if (trickScore >= SURVIVAL.GRADE_S) grade = 'S';
    else if (trickScore >= SURVIVAL.GRADE_A) grade = 'A';
    else if (trickScore >= SURVIVAL.GRADE_B) grade = 'B';
    else if (trickScore >= SURVIVAL.GRADE_C) grade = 'C';

    // Build a single centered stat card
    for (const cc of this.cardContainers) {
      while (cc.children.length > 0) {
        cc.children[0].destroy();
        cc.removeChildAt(0);
      }
    }

    // Use first card container, centered
    const cc = this.cardContainers[0];
    if (this.cardContainers[1]) this.cardContainers[1].visible = false;

    const p = state.players[0];
    const pColor = PLAYER_COLORS[0];
    const tank = lookupTank(p.tankId);
    const stats = state.matchStats;

    // Card background
    const bg = new Graphics();
    bg.roundRect(0, 0, PM.CARD_WIDTH, PM.CARD_HEIGHT, PM.CARD_CORNER_RADIUS);
    bg.fill({ color: 0x0a0a15, alpha: PM.CARD_BG_ALPHA });
    bg.roundRect(0, 0, PM.CARD_WIDTH, PM.CARD_HEIGHT, PM.CARD_CORNER_RADIUS);
    bg.stroke({ color: 0xff4444, width: PM.CARD_BORDER_WIDTH, alpha: 0.7 });
    cc.addChild(bg);

    // Tank name header
    const nameText = new Text({
      text: `${tank.name.toUpperCase()}`,
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: PM.TANK_NAME_FONT_SIZE,
        fontWeight: 'bold',
        fill: pColor,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    nameText.position.set(16, 12);
    cc.addChild(nameText);

    // Grade badge
    const gradeBadge = new Text({
      text: `GRADE: ${grade}`,
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 11,
        fontWeight: 'bold',
        fill: grade === 'S' ? 0xffd700 : grade === 'A' ? 0x44ff88 : grade === 'B' ? 0x44aaff : 0x888888,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    gradeBadge.anchor.set(1, 0);
    gradeBadge.position.set(PM.CARD_WIDTH - 12, 14);
    cc.addChild(gradeBadge);

    // Divider
    const divider = new Graphics();
    divider.moveTo(16, 40);
    divider.lineTo(PM.CARD_WIDTH - 16, 40);
    divider.stroke({ color: chrome, width: 1, alpha: 0.3 });
    cc.addChild(divider);

    // Stats rows
    const statRows = [
      { label: 'Kills', value: `${kills}` },
      { label: 'Trick Score', value: `${trickScore.toLocaleString()}` },
      { label: 'Damage Dealt', value: `${Math.round(stats.damageDealt[0])}%` },
      { label: 'Best Hit', value: stats.bestSingleHit[0] > 0 ? `${Math.round(stats.bestSingleHit[0])}%` : '--' },
      { label: 'Shots Fired', value: `${stats.shotsFired[0]}` },
    ];

    let rowY = 52;
    for (const row of statRows) {
      const labelTx = new Text({
        text: row.label,
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: PM.STAT_FONT_SIZE,
          fill: PM.STAT_LABEL_COLOR,
        }),
      });
      labelTx.position.set(16, rowY);
      cc.addChild(labelTx);

      const valTx = new Text({
        text: row.value,
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: PM.STAT_VALUE_FONT_SIZE,
          fontWeight: 'bold',
          fill: PM.STAT_VALUE_COLOR,
          stroke: { color: 0x000000, width: 1 },
        }),
      });
      valTx.anchor.set(1, 0);
      valTx.position.set(PM.CARD_WIDTH - 16, rowY);
      cc.addChild(valTx);

      rowY += 28;
    }

    // High score line
    if (hs) {
      const hsText = new Text({
        text: isNewBest ? 'NEW BEST!' : `Best: ${hs.kills} kills`,
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 13,
          fontWeight: 'bold',
          fill: isNewBest ? 0xffd700 : 0x666666,
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      hsText.position.set(16, rowY + 4);
      cc.addChild(hsText);
    }

    // Awards
    while (this.awardContainer.children.length > 0) {
      this.awardContainer.children[0].destroy();
      this.awardContainer.removeChildAt(0);
    }

    const awards = computeAwards(state);
    if (awards.length > 0) {
      const header = new Text({
        text: 'AWARDS',
        style: new TextStyle({
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 14,
          fontWeight: 'bold',
          fill: PM.AWARD_COLOR,
          stroke: { color: 0x000000, width: 2 },
        }),
      });
      header.anchor.set(0.5, 0);
      header.position.set(0, 0);
      this.awardContainer.addChild(header);

      let awardY = 28;
      for (const award of awards) {
        const awardPColor = PLAYER_COLORS[award.playerIndex];
        const row = new Text({
          text: `${award.icon}  ${award.label}    ${award.detail}`,
          style: new TextStyle({
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: PM.AWARD_FONT_SIZE,
            fill: awardPColor,
            stroke: { color: 0x000000, width: 2 },
          }),
        });
        row.anchor.set(0.5, 0);
        row.position.set(0, awardY);
        this.awardContainer.addChild(row);
        awardY += PM.AWARD_ROW_HEIGHT;
      }
    }
  }

  destroy(): void {
    for (const cc of this.cardContainers) cc.destroy({ children: true });
    this.awardContainer.destroy({ children: true });
    this.buttonContainer.destroy({ children: true });
    this.statusText.destroy();
    this.coinText.destroy();
    this.overlay.destroy();
    this.winnerText.destroy();
    this.roundText.destroy();
    this.container.destroy();
  }
}
